import type {
  VersionDeltaLike,
  VersionDetailLike,
  VersionDiffStatus,
  VersionExecutionPayloadLike,
  VersionSnapshotCell,
} from "./types";

import type { SmartSheet, ScoreResult } from "@/queries/smart-tables/types";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

export const EMPTY_PREVIEW_DELTAS: VersionDeltaLike[] = [];

const toRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
};

export const isSaveMutationForTable = (
  variables: unknown,
  tableId: string,
  mutationKey?: readonly unknown[],
): boolean => {
  const isKeyedTableSave = mutationKey?.[0] === "smart-table-save" && mutationKey?.[1] === tableId;
  if (isKeyedTableSave) {
    return true;
  }

  const vars = toRecord(variables);
  if (!vars || vars.tableId !== tableId) {
    return false;
  }

  if ("force_recompute" in vars || "stale_only" in vars || "confirmation_token" in vars) {
    return false;
  }

  return true;
};

export const getInitialRunningSheetIds = (sheetsData?: SmartSheet[] | null): Set<string> => {
  const initialRunningSheetIds = new Set<string>();

  for (const sheet of sheetsData ?? []) {
    const hasActiveExecution =
      sheet.has_active_execution === true ||
      (typeof sheet.active_execution_count === "number" && sheet.active_execution_count > 0) ||
      (typeof sheet.pending_cell_count === "number" && sheet.pending_cell_count > 0) ||
      (Array.isArray(sheet.active_execution_ids) && sheet.active_execution_ids.length > 0);

    if (hasActiveExecution) {
      initialRunningSheetIds.add(sheet.id);
    }
  }

  return initialRunningSheetIds;
};

export const getMostRecentSheetUpdatedAt = (sheetsData?: SmartSheet[] | null): Date | null => {
  if (!sheetsData || sheetsData.length === 0) {
    return null;
  }

  let mostRecent: Date | null = null;

  for (const sheet of sheetsData) {
    const updatedAt = toRecord(sheet)?.updated_at;

    if (
      typeof updatedAt !== "string" &&
      typeof updatedAt !== "number" &&
      !(updatedAt instanceof Date)
    ) {
      continue;
    }

    const parsedDate = new Date(updatedAt);
    if (Number.isNaN(parsedDate.getTime())) {
      continue;
    }

    if (!mostRecent || parsedDate > mostRecent) {
      mostRecent = parsedDate;
    }
  }

  return mostRecent;
};

const formatScoreSummaryValue = (value: number): string => {
  if (value >= 0 && value <= 1) {
    return `${(value * 100).toFixed(1)}%`;
  }

  return value.toFixed(2);
};

const formatScoreCount = (successCount: number, totalCount: number): string | null => {
  if (totalCount <= 0) {
    return null;
  }

  return `${successCount.toLocaleString()}/${totalCount.toLocaleString()}`;
};

const getAggregateWinnerLabel = (scoreResult?: ScoreResult | null): string | null => {
  const detailsAggregateResult = isRecord(scoreResult?.details?.aggregate_result)
    ? scoreResult.details.aggregate_result
    : null;
  const aggregate = isRecord(scoreResult?.aggregate) ? scoreResult.aggregate : null;
  const aggregateResult = isRecord(aggregate?.result) ? aggregate.result : null;
  const isAggregateScoreMode = scoreResult?.score_type === "aggregate";

  if (!isAggregateScoreMode && !detailsAggregateResult && !aggregateResult) {
    return null;
  }

  const winner = detailsAggregateResult?.winner ?? aggregateResult?.winner ?? aggregate?.value;
  if (typeof winner === "string" && winner.trim()) {
    return winner;
  }

  if (typeof winner === "number") {
    return formatScoreSummaryValue(winner);
  }

  return null;
};

const getBooleanScoreCountLabel = (scoreResult?: ScoreResult | null): string | null => {
  const aggregate = isRecord(scoreResult?.aggregate) ? scoreResult.aggregate : null;
  const aggregateSuccessCount = aggregate?.success_count;
  const aggregateTotalCount = aggregate?.total_count;
  if (typeof aggregateSuccessCount === "number" && typeof aggregateTotalCount === "number") {
    return formatScoreCount(aggregateSuccessCount, aggregateTotalCount);
  }

  const columns = Array.isArray(scoreResult?.columns) ? scoreResult.columns : [];
  const columnCounts = columns.reduce(
    (acc, column) => {
      const explicitSuccessCount = column.success_count;
      const explicitTotalCount = column.total_count;
      const fallbackTotalCount =
        column.resolved_scoring_type === "boolean" ? column.included_count : undefined;
      const fallbackSuccessCount =
        typeof column.score === "number" && typeof fallbackTotalCount === "number"
          ? Math.round(column.score * fallbackTotalCount)
          : undefined;
      const successCount =
        typeof explicitSuccessCount === "number" ? explicitSuccessCount : fallbackSuccessCount;
      const totalCount =
        typeof explicitTotalCount === "number" ? explicitTotalCount : fallbackTotalCount;

      if (typeof successCount !== "number" || typeof totalCount !== "number") {
        return acc;
      }

      return {
        successCount: acc.successCount + successCount,
        totalCount: acc.totalCount + totalCount,
      };
    },
    { successCount: 0, totalCount: 0 },
  );

  return formatScoreCount(columnCounts.successCount, columnCounts.totalCount);
};

export const getScoreSummary = (scoreResult?: ScoreResult | null) => {
  let scoreSummaryLabel =
    getAggregateWinnerLabel(scoreResult) ?? getBooleanScoreCountLabel(scoreResult) ?? "—";

  if (scoreSummaryLabel === "—") {
    let scoreSummaryValue: number | null = null;
    if (typeof scoreResult?.overall_score === "number") {
      scoreSummaryValue = scoreResult.overall_score;
    } else if (typeof scoreResult?.aggregate?.value === "number") {
      scoreSummaryValue = scoreResult.aggregate.value;
    }

    if (typeof scoreSummaryValue === "number") {
      scoreSummaryLabel = formatScoreSummaryValue(scoreSummaryValue);
    }
  }

  const isScoreRecalculating =
    scoreResult?.status === "queued" || scoreResult?.status === "running";

  return {
    scoreSummaryLabel,
    isScoreRecalculating,
  };
};

export const getPreviewVersionLabel = ({
  previewVersionData,
  intendedPreviewVersionId,
  versionsList,
}: {
  previewVersionData?: VersionDetailLike;
  intendedPreviewVersionId?: string;
  versionsList?: VersionDetailLike[];
}): string | undefined => {
  if (previewVersionData) {
    return previewVersionData.name || `Version ${previewVersionData.version_number}`;
  }

  if (intendedPreviewVersionId && versionsList) {
    const intended = versionsList.find(v => v.id === intendedPreviewVersionId);
    if (intended) {
      return intended.name || `Version ${intended.version_number}`;
    }
  }

  return undefined;
};

export interface VersionExecutionSummary {
  runs: number;
  statuses: string[];
  executionTouchedCells: number;
}

export interface VersionExecutionContextSummary {
  hasExecutionContext: boolean;
  runs: number;
  statuses: string[];
  changedCellCount: number;
}

export interface VersionDeltaDigest {
  summary: {
    totalDeltas: number;
    structuralChanges: number;
    contentChanges: number;
  };
  rows: {
    added: number;
    removed: number;
    net: number;
  };
  columns: {
    added: number;
    removed: number;
    updated: number;
    dependencyChanges: number;
    net: number;
  };
  cells: {
    edited: number;
    computedTouched: number;
    totalTouched: number;
  };
  metadata: {
    sheetUpdated: boolean;
    scoreConfigUpdated: boolean;
  };
  execution: {
    runs: number;
    statuses: string[];
  };
}

const getNumericCandidate = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  return null;
};

const getVersionScoreValue = (version?: VersionDetailLike): number | null => {
  if (!version || !isRecord(version)) {
    return null;
  }

  const versionRecord = version as Record<string, unknown>;
  const summaryValue = versionRecord.summary;
  const metadataValue = versionRecord.metadata;

  const scoreCandidates: unknown[] = [
    versionRecord.overall_score,
    versionRecord.score,
    versionRecord.aggregate_score,
    isRecord(summaryValue) ? summaryValue.score : null,
    isRecord(metadataValue) ? metadataValue.score : null,
  ];

  for (const candidate of scoreCandidates) {
    const numeric = getNumericCandidate(candidate);
    if (numeric !== null) {
      return numeric;
    }

    if (isRecord(candidate)) {
      const nestedNumeric =
        getNumericCandidate(candidate.overall_score) ??
        getNumericCandidate(candidate.aggregate_score) ??
        getNumericCandidate(candidate.score) ??
        getNumericCandidate(candidate.value);

      if (nestedNumeric !== null) {
        return nestedNumeric;
      }
    }
  }

  return null;
};

export const getPreviewScoreSummaryLabel = (
  previewVersionData?: VersionDetailLike,
  intendedPreviewVersionId?: string,
  versionsList?: VersionDetailLike[],
): string => {
  const fallbackVersion =
    intendedPreviewVersionId && versionsList
      ? versionsList.find(version => version.id === intendedPreviewVersionId)
      : undefined;

  const scoreValue = getVersionScoreValue(previewVersionData ?? fallbackVersion);

  if (scoreValue === null) {
    return "—";
  }

  if (scoreValue >= 0 && scoreValue <= 1) {
    return `${(scoreValue * 100).toFixed(1)}%`;
  }

  return scoreValue.toFixed(2);
};

export const getPreviewVersionSavedAt = ({
  previewVersionData,
  intendedPreviewVersionId,
  versionsList,
}: {
  previewVersionData?: VersionDetailLike;
  intendedPreviewVersionId?: string;
  versionsList?: VersionDetailLike[];
}): Date | null => {
  const fallbackVersion =
    intendedPreviewVersionId && versionsList
      ? versionsList.find(version => version.id === intendedPreviewVersionId)
      : undefined;

  const rawDate = previewVersionData?.created_at ?? fallbackVersion?.created_at;

  if (!rawDate) {
    return null;
  }

  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
};

const getExecutionPayload = (delta: VersionDeltaLike): VersionExecutionPayloadLike | null => {
  if (delta.change_type !== "cells_computed" || !isRecord(delta.payload)) {
    return null;
  }

  return delta.payload as VersionExecutionPayloadLike;
};

export const getVersionCellValue = (cell?: VersionSnapshotCell): string => {
  if (!cell) {
    return "";
  }

  if (cell.display_value !== null && cell.display_value !== undefined) {
    return cell.display_value;
  }

  if (cell.value === null || cell.value === undefined) {
    return "";
  }

  if (
    typeof cell.value === "string" ||
    typeof cell.value === "number" ||
    typeof cell.value === "boolean"
  ) {
    return String(cell.value);
  }

  if (isRecord(cell.value) && "value" in cell.value) {
    const nestedValue = cell.value.value;
    if (
      typeof nestedValue === "string" ||
      typeof nestedValue === "number" ||
      typeof nestedValue === "boolean"
    ) {
      return String(nestedValue);
    }
  }

  try {
    return JSON.stringify(cell.value, null, 2);
  } catch {
    return String(cell.value);
  }
};

export const normalizeVersionCellForDiff = (cell?: VersionSnapshotCell): string => {
  if (!cell) {
    return "";
  }

  if (cell.display_value !== null && cell.display_value !== undefined) {
    return String(cell.display_value);
  }

  if (cell.value === null || cell.value === undefined) {
    return "";
  }

  if (
    typeof cell.value === "string" ||
    typeof cell.value === "number" ||
    typeof cell.value === "boolean"
  ) {
    return String(cell.value);
  }

  try {
    return JSON.stringify(cell.value);
  } catch {
    return String(cell.value);
  }
};

export const getDiffCellStyle = (
  status?: VersionDiffStatus,
): { backgroundColor: string } | undefined => {
  if (status === "added") {
    return { backgroundColor: "rgba(34, 197, 94, 0.1)" };
  }

  if (status === "removed") {
    return { backgroundColor: "rgba(239, 68, 68, 0.1)" };
  }

  if (status === "updated") {
    return { backgroundColor: "rgba(245, 158, 11, 0.12)" };
  }

  return undefined;
};

export const buildExecutionSummary = (
  deltas?: VersionDeltaLike[] | null,
  baselineTouchedCells = 0,
): VersionExecutionSummary => {
  const executionIds = new Set<string>();
  const statuses = new Set<string>();
  const touched = new Set<string>();

  for (const delta of deltas ?? []) {
    const payload = getExecutionPayload(delta);
    if (!payload) {
      continue;
    }

    const executionId = payload.execution_id;
    if (typeof executionId === "string" && executionId.length > 0) {
      executionIds.add(executionId);
    }

    const status = payload.execution_status;
    if (typeof status === "string" && status.length > 0) {
      statuses.add(status);
    }

    const changedCells = payload.changed_cells;
    if (!Array.isArray(changedCells)) {
      continue;
    }

    for (const changedCell of changedCells) {
      if (!changedCell || typeof changedCell !== "object") {
        continue;
      }

      const rowIndex = (changedCell as { row_index?: unknown }).row_index;
      const columnId = (changedCell as { column_id?: unknown }).column_id;

      if (typeof rowIndex === "number" && typeof columnId === "string" && columnId.length > 0) {
        touched.add(`${rowIndex}:${columnId}`);
      }
    }
  }

  return {
    runs: executionIds.size,
    statuses: Array.from(statuses),
    executionTouchedCells: Math.max(baselineTouchedCells, touched.size),
  };
};

export const buildExecutionContextSummary = (
  deltas?: VersionDeltaLike[] | null,
): VersionExecutionContextSummary => {
  const executionSummary = buildExecutionSummary(deltas);

  return {
    hasExecutionContext:
      executionSummary.runs > 0 ||
      executionSummary.statuses.length > 0 ||
      executionSummary.executionTouchedCells > 0,
    runs: executionSummary.runs,
    statuses: executionSummary.statuses,
    changedCellCount: executionSummary.executionTouchedCells,
  };
};

export const buildVersionDeltaDigest = (deltas?: VersionDeltaLike[] | null): VersionDeltaDigest => {
  let rowAdded = 0;
  let rowRemoved = 0;

  let colAdded = 0;
  let colRemoved = 0;
  let colUpdated = 0;
  let dependencyChanges = 0;

  let editedCells = 0;
  let computedTouched = 0;

  let sheetUpdated = false;
  let scoreConfigUpdated = false;

  let structuralChanges = 0;
  let contentChanges = 0;

  for (const delta of deltas ?? []) {
    const changeType = delta.change_type ?? "";
    const payload = toRecord(delta.payload);

    if (changeType.includes("row") && changeType.includes("add")) {
      rowAdded += 1;
      structuralChanges += 1;
      continue;
    }

    if (
      changeType.includes("row") &&
      (changeType.includes("remove") || changeType.includes("delete"))
    ) {
      rowRemoved += 1;
      structuralChanges += 1;
      continue;
    }

    if (changeType.includes("column") && changeType.includes("add")) {
      colAdded += 1;
      structuralChanges += 1;
      continue;
    }

    if (
      changeType.includes("column") &&
      (changeType.includes("remove") || changeType.includes("delete"))
    ) {
      colRemoved += 1;
      structuralChanges += 1;
      continue;
    }

    if (changeType.includes("dependency")) {
      dependencyChanges += 1;
      structuralChanges += 1;
      continue;
    }

    if (
      changeType.includes("column") &&
      (changeType.includes("update") || changeType.includes("edit"))
    ) {
      colUpdated += 1;
      structuralChanges += 1;
      continue;
    }

    if (changeType === "cell_edit") {
      editedCells += 1;
      contentChanges += 1;
      continue;
    }

    if (changeType === "cells_computed") {
      if (payload && Array.isArray(payload.changed_cells)) {
        computedTouched += payload.changed_cells.length;
      }
      contentChanges += 1;
      continue;
    }

    if (
      changeType.includes("sheet") &&
      (changeType.includes("update") || changeType.includes("edit"))
    ) {
      sheetUpdated = true;
      structuralChanges += 1;
      continue;
    }

    if (changeType.includes("score") && changeType.includes("config")) {
      scoreConfigUpdated = true;
      structuralChanges += 1;
      continue;
    }
  }

  const executionSummary = buildExecutionSummary(deltas, computedTouched);

  return {
    summary: {
      totalDeltas: (deltas ?? []).length,
      structuralChanges,
      contentChanges,
    },
    rows: {
      added: rowAdded,
      removed: rowRemoved,
      net: rowAdded - rowRemoved,
    },
    columns: {
      added: colAdded,
      removed: colRemoved,
      updated: colUpdated,
      dependencyChanges,
      net: colAdded - colRemoved,
    },
    cells: {
      edited: editedCells,
      computedTouched,
      totalTouched: editedCells + computedTouched,
    },
    metadata: {
      sheetUpdated,
      scoreConfigUpdated,
    },
    execution: {
      runs: executionSummary.runs,
      statuses: executionSummary.statuses,
    },
  };
};
